// Legacy vertices are polygon 0 / ring 0. Ring 0 is an outer edge;
// subsequent rings are exclusions. Indices preserve topology in the existing JSON contract.
export function boundaryRings(points = []) {
  const rings = new Map();
  points.forEach((point) => {
    const polygon = point.polygon_index ?? 0;
    const ring = point.ring_index ?? 0;
    const key = `${polygon}:${ring}`;
    if (!rings.has(key)) rings.set(key, { polygon, ring, points: [] });
    rings.get(key).points.push({ lat: point.lat, lng: point.lng });
  });
  return Array.from(rings.values()).sort((a, b) => a.polygon - b.polygon || a.ring - b.ring);
}

export function flattenBoundaryRings(rings) {
  const simple = rings.length === 1 && rings[0].polygon === 0 && rings[0].ring === 0;
  return rings.flatMap(({ polygon, ring, points }) => points.map((point) => simple
    ? { lat: point.lat, lng: point.lng }
    : { lat: point.lat, lng: point.lng, polygon_index: polygon, ring_index: ring }));
}

export function boundaryPolygonPaths(points) {
  const polygons = new Map();
  boundaryRings(points).forEach(({ polygon, ring, points: ringPoints }) => {
    if (!polygons.has(polygon)) polygons.set(polygon, []);
    // Google Maps needs holes wound opposite to their outer ring.
    const signedArea = ringPoints.reduce((sum, point, index) => {
      const next = ringPoints[(index + 1) % ringPoints.length];
      return sum + point.lng * next.lat - next.lng * point.lat;
    }, 0);
    polygons.get(polygon).push((ring === 0 ? signedArea < 0 : signedArea > 0)
      ? [...ringPoints].reverse() : ringPoints);
  });
  return Array.from(polygons.values());
}

export function sphericalRingArea(points) {
  const radians = (value) => value * Math.PI / 180;
  const sum = points.reduce((total, point, index) => {
    const next = points[(index + 1) % points.length];
    return total + radians(next.lng - point.lng) * (2 + Math.sin(radians(point.lat)) + Math.sin(radians(next.lat)));
  }, 0);
  return Math.abs(sum * 6378137 ** 2 / 2);
}

export function boundaryAcres(points, computeArea = sphericalRingArea) {
  return boundaryRings(points).reduce((total, { ring, points: vertices }) =>
    total + (ring === 0 ? 1 : -1) * Math.abs(computeArea(vertices)), 0) * 0.000247105;
}

export function parseBoundaryFile(text, filename) {
  let polygons = [];
  if (/\.kml$/i.test(filename)) {
    const document = new DOMParser().parseFromString(text, "application/xml");
    if (document.getElementsByTagName("parsererror").length) throw new Error("The KML file is not valid XML.");
    const elements = (root, tag) => Array.from(root.getElementsByTagNameNS("*", tag));
    const readRing = (root) => {
      const value = elements(root, "coordinates")[0]?.textContent || "";
      return value.trim().split(/\s+/).map((entry) => entry.split(",").map(Number));
    };
    polygons = elements(document, "Polygon").map((polygon) => {
      const outer = elements(polygon, "outerBoundaryIs");
      if (outer.length !== 1) throw new Error("Each polygon must have one outer boundary.");
      return [readRing(outer[0]), ...elements(polygon, "innerBoundaryIs").map(readRing)];
    });
  } else {
    const data = JSON.parse(text);
    if (data.crs && !/4326|CRS84/i.test(JSON.stringify(data.crs))) throw new Error("Export boundaries in WGS84 latitude/longitude (EPSG:4326).");
    const visit = (geometry) => {
      if (geometry?.type === "FeatureCollection") return geometry.features.forEach(visit);
      if (geometry?.type === "Feature") return visit(geometry.geometry);
      if (geometry?.type === "Polygon") polygons.push(geometry.coordinates);
      else if (geometry?.type === "MultiPolygon") polygons.push(...geometry.coordinates);
      else throw new Error("Use a GeoJSON Polygon or MultiPolygon boundary export.");
    };
    visit(data);
  }
  if (!polygons.length) throw new Error("No polygon boundaries were found.");
  let count = 0;
  const rings = polygons.flatMap((polygon, polygonIndex) => polygon.map((coordinates, ring) => {
    let points = coordinates.map(([lng, lat]) => {
      count += 1;
      if (!Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) {
        throw new Error("Use WGS84 latitude/longitude coordinates.");
      }
      return { lat, lng };
    });
    if (points.length > 1 && points[0].lat === points.at(-1).lat && points[0].lng === points.at(-1).lng) points.pop();
    if (points.length < 3) throw new Error("Each boundary needs at least three vertices.");
    return { polygon: polygonIndex, ring, points };
  }));
  if (count > 10000) throw new Error("Use a boundary export with at most 10,000 vertices.");
  return flattenBoundaryRings(rings);
}
